import {
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MirroredRepeatWrapping,
  PlaneGeometry,
  Quaternion,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
} from 'three';

import type { CoordConfig, Rawdata } from '@/types';

import { roundDecimals } from '../math';
import { RawLoadOptions, RawObject, RawObjectOptions, RawObjectTheme } from './RawObject';

export class RawImage extends RawObject {
  constructor(data: Rawdata) {
    super(data.name, data.type);
  }

  /**
   * 根据最大宽度创建一个默认图片渲染方法：图片左上角与 three.js 坐标系中心点重合，渲染宽度阈值为 maxWidth
   * @param maxWidth 最大宽度
   * @returns 一个渲染函数，用于 Stage 的初始化入参
   */
  static createRenderMethod(maxWidth: number): (data: Rawdata) => CoordConfig {
    return (data: Rawdata) => {
      const { width, height } = data.meta.image!;
      const renderWidth = Math.min(maxWidth, width);
      const ratio = renderWidth / width;

      const mat = new Matrix4().compose(new Vector3(0, 0, 0), new Quaternion(), new Vector3(ratio, -ratio, 1));

      return {
        position: [renderWidth / 2, -(height * ratio) / 2, 0],
        scale: [ratio, ratio, 1],
        matrixs: [mat.clone().invert(), mat],
      };
    };
  }

  /**
   * 根据画布尺寸与图片显示尺寸，计算合适的缩放比例，使得图片保持高宽比的情况下能填充画布
   * @param canvasWidth 画布宽度
   * @param canvasHeight 画布高度
   * @param file 图片源
   * @param scale 渲染比例
   * @returns
   */
  static getFitZoom(canvasWidth: number, canvasHeight: number, file: Rawdata, scale: Required<CoordConfig>['scale']) {
    if (file.type !== 'image') return 1;

    const renderWidth = file.meta.image!.width * scale[0];
    const renderHeight = file.meta.image!.height * scale[1];

    return roundDecimals(Math.min(canvasWidth / renderWidth, canvasHeight / renderHeight), 3);
  }

  async load(data: Rawdata, { coord, onProgress }: RawLoadOptions) {
    const { meta } = data;
    const { position, scale } = coord;

    const loader = new TextureLoader();
    const texture = await loader.loadAsync(data.url, (progress) => {
      if (!onProgress) return;
      const { total, loaded } = progress;
      if (total === loaded) {
        onProgress({ info: 'finish' });
      } else {
        onProgress({ info: 'downloading' });
      }
    });

    texture.wrapT = MirroredRepeatWrapping;
    texture.colorSpace = SRGBColorSpace;

    const target = new Mesh(
      new PlaneGeometry(meta.image!.width * scale[0], meta.image!.height * scale[1]),
      new MeshBasicMaterial({ map: texture })
    );
    target.name = data.name;
    target.position.set(position[0], position[1], -1);
    this.object.add(target);

    return { cameraPosition: position };
  }

  updateRenderOptions(options: RawObjectOptions, forceUpdate = false) {
    this.options = Object.assign(this.options, options);

    let key: keyof RawObjectOptions;
    for (key in options) {
      switch (key) {
        case 'colorTheme':
          this.setColorTheme(options[key] as RawObjectTheme);
          break;
        default:
          break;
      }
    }
  }

  setColorTheme(theme: RawObjectTheme) {}
}
